import React from 'react';

const RiskManagement = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  // COMPLETE MODULE CONTENT - ALL 4 MODULES FULLY IMPLEMENTED
  const moduleContent = {
    1: {
      title: "Position Sizing Fundamentals",
      icon: "📏",
      content: {
        keyPoints: [
          "Never risk more than 1-3% of your portfolio on a single trade",
          "Calculate position size based on your stop loss",
          "Smaller positions for higher risk trades",
          "Account size determines your maximum risk per trade"
        ],
        example: "$10,000 account. Risk 2% = $200 max per trade. If your stop loss is $100, buy 2 contracts max."
      },
      quiz: [
        {
          id: 'q1',
          question: "What percentage of your portfolio should you risk on a single trade?",
          options: ["10-20%", "25-50%", "1-3%", "Everything available"],
          correct: 2,
          explanation: "Correct! Professional traders typically risk only 1-3% of their portfolio on any single trade to preserve capital."
        },
        {
          id: 'q2',
          question: "How do you calculate position size?",
          options: ["Random guess", "Maximum you can afford", "Account risk ÷ Trade risk", "Always the same amount"],
          correct: 2,
          explanation: "Correct! Position size = (Account Risk Amount) ÷ (Risk per contract/share). This ensures consistent risk management."
        }
      ]
    },
    2: {
      title: "Stop Losses & Exit Rules",
      icon: "🛑",
      content: {
        keyPoints: [
          "Always set a stop loss before entering a trade",
          "Stick to your predetermined exit point",
          "Don't move stops against you (no emotions!)",
          "Consider trailing stops for profitable trades"
        ],
        example: "Buy BTC call at $2,000. Set stop loss at $1,500 (25% loss). If it hits $1,500, exit immediately - no exceptions!"
      },
      quiz: [
        {
          id: 'q1',
          question: "What is a stop loss?",
          options: ["A guaranteed profit", "A predetermined exit point to limit losses", "A type of option", "A trading fee"],
          correct: 1,
          explanation: "Correct! A stop loss is a predetermined price level where you exit a losing trade to limit your losses."
        },
        {
          id: 'q2',
          question: "When should you set your stop loss?",
          options: ["After the trade goes against you", "Before entering the trade", "Only if losing money", "Never - hold forever"],
          correct: 1,
          explanation: "Correct! ALWAYS set your stop loss BEFORE entering a trade. This removes emotion from your exit decision."
        }
      ]
    },
    3: {
      title: "Risk-Reward Ratios",
      icon: "⚖️",
      content: {
        keyPoints: [
          "Risk-reward ratio compares potential loss to potential profit",
          "Aim for minimum 1:2 ratio (risk $1 to make $2)",
          "Higher ratios allow for lower win rates",
          "Calculate before entering any trade"
        ],
        example: "Risk $500 to make $1,500 = 1:3 ratio. Even with 30% win rate, you're profitable long-term."
      },
      quiz: [
        {
          id: 'q1',
          question: "What is the risk-reward ratio?",
          options: ["How much you risk vs potential profit", "The option premium", "The strike price", "The time to expiration"],
          correct: 0,
          explanation: "Correct! Risk-reward ratio compares how much you're risking versus how much you could potentially profit."
        },
        {
          id: 'q2',
          question: "What's a good minimum risk-reward ratio?",
          options: ["1:1", "1:2", "2:1", "1:0.5"],
          correct: 1,
          explanation: "Correct! A minimum 1:2 ratio means you risk $1 to potentially make $2, giving you better long-term odds."
        }
      ]
    },
    4: {
      title: "Portfolio Protection Strategies",
      icon: "🛡️",
      content: {
        keyPoints: [
          "Diversify across different strategies and timeframes",
          "Never put all capital in one trade or strategy",  
          "Use correlation analysis to avoid related risks",
          "Keep cash reserves for opportunities"
        ],
        example: "Don't buy 10 BTC calls all expiring same day. Spread across different strikes and expirations to reduce risk."
      },
      quiz: [
        {
          id: 'q1',
          question: "Why is diversification important?",
          options: ["To maximize profits", "To reduce overall portfolio risk", "To increase complexity", "To impress others"],
          correct: 1,
          explanation: "Correct! Diversification reduces overall portfolio risk by not putting all your eggs in one basket."
        },
        {
          id: 'q2',
          question: "What percentage of your account should you keep in cash?",
          options: ["0% - invest everything", "10-20% for opportunities", "50% always", "90% - barely trade"],
          correct: 1,
          explanation: "Correct! Keeping 10-20% in cash reserves allows you to take advantage of new opportunities without over-leveraging."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Essential risk management principles</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-red-500 pl-4">
            <h4 className="text-white font-bold mb-2">⚠️ Critical Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
            <h5 className="text-blue-400 font-bold mb-2">📊 Practical Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Risk Management Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these concepts to protect your capital</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-red-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You understand {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `risk_management_basics_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Risk Manager'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'risk_management_basics'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Risk Management Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Risk management is critical - please review and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default RiskManagement;
